/* eslint-disable @typescript-eslint/no-explicit-any */
import { createContext, useContext, useEffect, useReducer, useRef, useState } from "react";
import useAuthHttp from "../hooks/useAuthHttp";
import { useAuthContext } from "./AuthContext";
import { AssetGoalUser, IHomeResponse, PortfolioProfitBenchmark, PortfolioProfitEvolutionPeriod, PortfolioUser, VariableIncomeList } from "../responses/home";
import { useQuery } from "@tanstack/react-query";
import { AssetFactory, ICashflowModel, IFixedIncomeModel, IPropertyModel, IVariableIncomeModel, IVehicleModel, Wallet } from "../models/asset";
import AcceptPolicyAndTermsDialog from "../components/AcceptPolicyAndTermsDialog";
import MainLoader from "../layout/MainLoader";
import { AnalyticsEvent, Tracker } from "../analytics";
import {
    needsTermsAcceptance,
    resolveTermsStatusFailureReason,
    type TermsStatusResponse,
} from "../http/terms-status";
import {
    consumeSignupPendingConversion,
    invalidateSignupPendingOnTermsUpdate,
    syncSignupPendingAfterOAuthTermsCheck,
} from "../services/signupConversionSession";
import { collectProcessingAssetIds, processingRefetchInterval } from "../commons/asset-processing";

interface IHomePayload {
    portfolio: PortfolioProfitEvolutionPeriod|null;
    goal: AssetGoalUser|null;
    wallet: IWallet|null;
    variableIncome: VariableIncomeList[];
    variableIncomeModel: IVariableIncomeModel[];
    variableIncomeGrouppedModel: IVariableIncomeModel[][];
    fixedIncomeModel: IFixedIncomeModel[];
    fixedIncomeGrouppedModel: IFixedIncomeModel[][];
    cashflowModel: ICashflowModel[];
    cashflowGrouppedModel: ICashflowModel[][];
    vehicleModel: IVehicleModel[];
    propertyModel: IPropertyModel[];
    benchmark: PortfolioProfitBenchmark[];
    hasProcessingAssets: boolean;
    processingAssetIds: string[];
}

interface IHomeState extends IHomePayload {
    isLoading: boolean;
}

interface IWallet {
    totalValue: number;
    capitalGainValue: number;
    fixedIncomeTotalValue: number;
    fixedIncomeTotalPercentage: number;
    variableIncomeTotalValue: number;
    variableIncomeTotalPercentage: number;
    cashUserTotalValue: number;
    cashUserTotalPercentage: number;
    vehiclesTotalValue: number;
    vehiclesTotalPercentage: number;
    propertiesTotalValue: number;
    propertiesTotalPercentage: number;
}

interface IHomeContext extends IHomeState {
    load: () => void;
    refresh: () => Promise<void>;
}

const HomeContext = createContext<IHomeContext|null>(null);

const initialState: IHomeState = {
    isLoading: false,
    portfolio: null,
    goal: null,
    wallet: null,
    variableIncome: [],
    variableIncomeModel: [],
    variableIncomeGrouppedModel: [],
    fixedIncomeGrouppedModel: [],
    fixedIncomeModel: [],
    cashflowGrouppedModel: [],
    cashflowModel: [],
    vehicleModel: [],
    propertyModel: [],
    benchmark: [],
    hasProcessingAssets: false,
    processingAssetIds: []
}

const reducer = (state: IHomeState, action: { type: string; payload: IHomePayload|null; }) => {
    switch (action.type) {
        case "StartLoad":
            return {
                ...state
            }
        case "Load":
            return {
                ...state,
                ...action.payload
            }
        default:
            throw new Error(`Type ${action.type} is not implemented.`);
    }
}

interface IHomeProviderProps {
    children: (isLoading: boolean) => React.ReactNode;
}

const HomeProvider = ({ children }: IHomeProviderProps) => {
    const [state, dispatch] = useReducer(reducer, initialState);
    const { user, logout, isAuthenticated } = useAuthContext();
    const { doGet } = useAuthHttp();
    const [termsAcceptedLocally, setTermsAcceptedLocally] = useState(false);
    const termsModalTrackedRef = useRef(false);
    const termsFailedTrackedRef = useRef<string | null>(null);

    const checkTermsQuery = useQuery({
        queryKey: ["user", user.user_id, "terms"],
        queryFn: async () => {
            await Tracker.ensureIdentified(user.user_id);
            Tracker.track(AnalyticsEvent.TermsStatusRequested);
            const response = await doGet<TermsStatusResponse>(`user/terms-status`);

            if (response.status > 299) {
                throw new Error("Error checking terms status");
            }

            return response;
        },
        enabled: isAuthenticated && !!user.user_id,
        staleTime: 0,
        refetchOnMount: "always",
    });

    const isTermsLoading =
        isAuthenticated &&
        (checkTermsQuery.isLoading || (checkTermsQuery.isFetching && !checkTermsQuery.data));

    const isTermsError = isAuthenticated && checkTermsQuery.isError;

    const mustAcceptTerms =
        checkTermsQuery.isSuccess &&
        needsTermsAcceptance(checkTermsQuery.data) &&
        !termsAcceptedLocally;

    const shouldShowTermsModal = isAuthenticated && mustAcceptTerms;

    const canRenderHome = checkTermsQuery.isSuccess && !mustAcceptTerms;

    useEffect(() => {
        if (!isTermsError || !checkTermsQuery.error) {
            return;
        }

        const reasonCode = resolveTermsStatusFailureReason(checkTermsQuery.error);
        const dedupeKey = `${reasonCode}-${checkTermsQuery.failureCount}`;

        if (termsFailedTrackedRef.current === dedupeKey) {
            return;
        }

        termsFailedTrackedRef.current = dedupeKey;
        Tracker.track(AnalyticsEvent.TermsStatusFailed, {
            reason_code: reasonCode,
            attempt_count: checkTermsQuery.failureCount,
        });
    }, [isTermsError, checkTermsQuery.error, checkTermsQuery.failureCount]);

    useEffect(() => {
        if (checkTermsQuery.isSuccess && checkTermsQuery.data) {
            syncSignupPendingAfterOAuthTermsCheck(checkTermsQuery.data);
        }
    }, [checkTermsQuery.isSuccess, checkTermsQuery.data]);

    useEffect(() => {
        if (!shouldShowTermsModal) {
            termsModalTrackedRef.current = false;
            return;
        }

        if (termsModalTrackedRef.current) {
            return;
        }

        termsModalTrackedRef.current = true;
        const requiresUpdate = checkTermsQuery.data?.requires_update === true;
        invalidateSignupPendingOnTermsUpdate(requiresUpdate);
        Tracker.track(AnalyticsEvent.TermsModalShown, {
            requires_update: requiresUpdate,
        });
    }, [shouldShowTermsModal, checkTermsQuery.data?.requires_update]);

    const homeQuery = useQuery({
        queryKey: ["user", user.user_id, "home"],
        enabled: canRenderHome || isTermsError,
        refetchOnWindowFocus: true,
        refetchInterval: (query) => {
            const portfolio = query.state.data?.portfolio_user;
            const processing = collectProcessingAssetIds(
                portfolio?.fixed_income_list,
                portfolio?.variable_income_list,
                portfolio?.cash_user_list
            );
            return processingRefetchInterval(processing.length > 0, (1000 * 60) * 3);
        },
        queryFn: async () => {
            return await doGet<IHomeResponse>(`user/${user.user_id}/home`);
        }
    });

    const dispatchLoad = (homeResponse: PortfolioUser|undefined) => {
        const totalValue = homeResponse?.portfolio_total_value ?? 0;

        const fixedIncomeTotalValue = homeResponse?.fixed_income_list
            ?.map(f => f.asset_current_value ?? 0)
            ?.reduce((a, b) => a + b, 0) ?? 0;

        const variableIncomeTotalValue = homeResponse?.variable_income_list
            ?.map(v => v.asset_current_value)
            ?.reduce((a, b) => a + b, 0) ?? 0;

        const cashUserTotalValue = homeResponse?.cash_user_list
            ?.map(c => c.asset_current_value)
            ?.reduce((a, b) => a + b, 0) ?? 0;

        const vehiclesTotalValue = homeResponse?.vehicles_user_list
            ?.map(v => v.vehicle_valuation_value)
            ?.reduce((a, b) => a + b, 0) ?? 0;

        const propertiesTotalValue = homeResponse?.property_user_list
            ?.map(p => p.property_actual_value ?? p.property_purchase_value)
            ?.reduce((a, b) => a + b, 0) ?? 0;

        const processingAssetIds = collectProcessingAssetIds(
            homeResponse?.fixed_income_list,
            homeResponse?.variable_income_list,
            homeResponse?.cash_user_list
        );

        const groupByArray = (array: any[], key: string | number): any[][] => {
            const grouped = array.reduce((acc, item) => {
                if (!acc[item[key]]) {
                    acc[item[key]] = [];
                }
                
                acc[item[key]].push(item);
                
                return acc;
            }, {});
            
            return Object.values(grouped);
        };

        dispatch({ 
            type: "Load", 
            payload: { 
                wallet: {
                    totalValue,
                    capitalGainValue: homeResponse?.portfolio_profit_benchmark
                        .filter(p => p.index_name.toUpperCase() == "PROFIT")
                        .map(p => p.month_profit_value ?? 0)
                        .reduce((a, b) => a + b, 0) ?? 0,
                    fixedIncomeTotalValue,
                    fixedIncomeTotalPercentage: (fixedIncomeTotalValue / totalValue) * 100,
                    variableIncomeTotalValue,
                    variableIncomeTotalPercentage: (variableIncomeTotalValue / totalValue) * 100,
                    cashUserTotalValue,
                    cashUserTotalPercentage: (cashUserTotalValue / totalValue) * 100,
                    vehiclesTotalValue,
                    vehiclesTotalPercentage: (vehiclesTotalValue / totalValue) * 100,
                    propertiesTotalValue,
                    propertiesTotalPercentage: (propertiesTotalValue / totalValue) * 100
                },
                portfolio: homeResponse?.portfolio_profit_evolution_period ?? null,
                goal: homeResponse?.asset_goal_user ?? null,
                variableIncome: homeResponse?.variable_income_list ?? [],
                variableIncomeModel: homeResponse?.variable_income_list?.map(v => 
                        AssetFactory.createFromVariable(v, new Wallet(totalValue))
                    ).filter(a => a != null) ?? [],
                variableIncomeGrouppedModel: groupByArray(homeResponse?.variable_income_list?.map(v => 
                        AssetFactory.createFromVariable(v, new Wallet(totalValue))
                    ).filter(a => a != null) ?? [], "title"),
                fixedIncomeModel: homeResponse?.fixed_income_list?.map(v => 
                        AssetFactory.createFromFixed(v, new Wallet(totalValue))
                    ).filter(a => a != null) ?? [],
                fixedIncomeGrouppedModel: groupByArray(homeResponse?.fixed_income_list?.map(v => 
                        AssetFactory.createFromFixed(v, new Wallet(totalValue))
                    ).filter(a => a != null) ?? [], "title"),
                cashflowModel: homeResponse?.cash_user_list?.map(v => 
                    AssetFactory.createFromCashflow(v, new Wallet(totalValue))
                ).filter(a => a != null) ?? [],
                cashflowGrouppedModel: groupByArray(homeResponse?.cash_user_list?.map(v => 
                    AssetFactory.createFromCashflow(v, new Wallet(totalValue))
                ).filter(a => a != null) ?? [], "title"),
                vehicleModel: homeResponse?.vehicles_user_list?.map(v => 
                    AssetFactory.createFromVehicle(v, new Wallet(totalValue))
                ).filter(a => a != null) ?? [],
                propertyModel: homeResponse?.property_user_list?.map(v => 
                    AssetFactory.createFromProperty(v, new Wallet(totalValue))
                ).filter(a => a != null) ?? [],
                benchmark: homeResponse?.portfolio_profit_benchmark ?? [],
                hasProcessingAssets: processingAssetIds.length > 0,
                processingAssetIds
            } 
        });
    }

    const load = async () => {
        dispatch({ type: "StartLoad", payload: null });

        const homeResponse = (await homeQuery.refetch()).data?.portfolio_user;

        dispatchLoad(homeResponse);
    };

    useEffect(() => {
        const homeResponse = homeQuery.data?.portfolio_user;
        dispatchLoad(homeResponse);

        if (homeQuery.isSuccess && consumeSignupPendingConversion()) {
            Tracker.trackSignUp('completed');
        }
    }, [homeQuery.data?.portfolio_user, homeQuery.isSuccess]);

    const termsModalVisible = shouldShowTermsModal;

    return (
        <>
            {isTermsLoading || (canRenderHome && homeQuery.isLoading) ? <MainLoader /> : null}

            <AcceptPolicyAndTermsDialog 
                termsUrl={checkTermsQuery.data?.terms_url ?? ""}
                privacyUrl={checkTermsQuery.data?.privacy_url ?? ""}
                show={termsModalVisible} 
                onClose={async (termsAccepted) => {
                    if (!termsAccepted) {
                        await logout();
                        return;
                    }

                    setTermsAcceptedLocally(true);
                }} 
            />

            <HomeContext.Provider 
                value={{ 
                    ...state, 
                    load, 
                    isLoading: homeQuery.isLoading, 
                    refresh: async () => { await homeQuery.refetch(); } 
                }}
            >
                {canRenderHome || isTermsError ? children(homeQuery.isLoading) : null}
            </HomeContext.Provider>
        </>
    );
}

const useHomeContext = () => {
    const context = useContext(HomeContext);

    if (!context)
        throw new Error(`HomeContext cannot be null`);

    return context;
}

export { HomeProvider, useHomeContext };
